import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { authService } from "@/services/authService";

const AuthContext = createContext(null);

// ctx: undefined = loading, null = not authenticated, object = authenticated
export function AuthProvider({ children }) {
  const [ctx, setCtx] = useState(undefined);

  const load = useCallback(async () => {
    try {
      setCtx(await authService.me());
    } catch {
      setCtx(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = useCallback(async (email, password) => {
    const data = await authService.login(email, password);
    setCtx(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await authService.register(payload);
    setCtx(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setCtx(null);
    }
  }, []);

  const hasPermission = useCallback(
    (perm) => !!ctx && (ctx.is_super_admin || (ctx.permissions || []).includes(perm)),
    [ctx]
  );

  // Memoized so consumers don't re-render on unrelated provider renders.
  const value = useMemo(
    () => ({ ctx, login, register, logout, hasPermission, reload: load }),
    [ctx, login, register, logout, hasPermission, load]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
